import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useTranslation } from "react-i18next";

/**
 * Compute { date_from, date_to } ISO strings (YYYY-MM-DD) for a given preset.
 *
 * IMPORTANT: emits LOCAL-calendar dates, not UTC. `toISOString()` normalises
 * to UTC and silently shifts the date by -1 for any user east of UTC+0
 * during late-evening hours (e.g. Cyprus UTC+3 at 18:00 local = 15:00 UTC of
 * the same day, but `today.setHours(0,0,0,0)` followed by `toISOString()`
 * yields the *previous* day's ISO date). Bookings stored as bare YYYY-MM-DD
 * strings then fail string-comparison filters and "Today" looks empty.
 */
const fmtLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export function getDateRange(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const fmt = fmtLocal;
  const add = (days) => {
    const d = new Date(today);
    d.setDate(d.getDate() + days);
    return d;
  };
  const dayOfWeek = today.getDay() || 7; // Mon=1..Sun=7
  const mondayThisWeek = add(-(dayOfWeek - 1));
  const sundayThisWeek = add(7 - dayOfWeek);
  const mondayNextWeek = add(7 - dayOfWeek + 1);
  const sundayNextWeek = add(7 - dayOfWeek + 7);
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const yesterday = add(-1);

  switch (preset) {
    case "today": return { date_from: fmt(today), date_to: fmt(today) };
    case "tomorrow": return { date_from: fmt(add(1)), date_to: fmt(add(1)) };
    case "this_week": return { date_from: fmt(mondayThisWeek), date_to: fmt(sundayThisWeek) };
    case "next_week": return { date_from: fmt(mondayNextWeek), date_to: fmt(sundayNextWeek) };
    case "this_month": return { date_from: fmt(firstOfMonth), date_to: fmt(lastOfMonth) };
    case "past": return { date_from: null, date_to: fmt(yesterday) };
    case "upcoming": return { date_from: fmt(today), date_to: null };
    default: return { date_from: null, date_to: null };
  }
}

export default function BookingDateFilter({ value, onChange }) {
  const { t } = useTranslation();
  const options = ["all", "upcoming", "today", "tomorrow", "this_week", "next_week", "this_month", "past"];
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-44" data-testid="booking-date-filter">
        <SelectValue placeholder={t("date_filter:label")} />
      </SelectTrigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt} value={opt} data-testid={`date-filter-${opt}`}>
            {t(`date_filter:${opt}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
